import type {
  Allocation,
  DetailedProposal,
  ProposalOrder,
} from '@moment/horizon-data-access/proposal'

interface ExtractedOrderDetails {
  id: string
  isin: string
  quantity: number
  price: number
  ytw: number | null
  amount: number
  description: string
  side: string
  sp_rating: string
}

export function extractOrderDetails(
  proposal: DetailedProposal | undefined,
  orderId?: string,
  accountId?: string,
  sleeveId?: string,
): ExtractedOrderDetails | null {
  if (!proposal || !orderId) return null

  const order = proposal.orders.find(
    (order: ProposalOrder) => order.moment_order_id === orderId,
  )

  if (!order) return null

  // If no accountId specified, return order details without allocation
  if (!accountId) {
    return {
      id: order.moment_order_id,
      isin: order.instrument.isin,
      quantity: 0, // No specific allocation
      price: order.limit_price,
      ytw: order.limit_ytw ?? null,
      amount: 0, // No specific allocation
      description: order.instrument.description ?? '',
      side: order.side,
      sp_rating: order.instrument.sp_rating ?? '',
    }
  }

  // Find applicable allocation
  const applicableAllocation = order.allocations.find(
    (allocation: Allocation) =>
      allocation.moment_account_id === accountId &&
      (sleeveId === undefined || allocation.sleeve_id === sleeveId),
  )

  if (!applicableAllocation) return null

  return {
    id: order.moment_order_id,
    isin: order.instrument.isin,
    quantity: applicableAllocation.quantity,
    price: order.limit_price,
    ytw: order.limit_ytw ?? null,
    amount: applicableAllocation.quantity * (order.limit_price / 100),
    description: order.instrument.description ?? '',
    side: order.side,
    sp_rating: order.instrument.sp_rating ?? '',
  }
}
